import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from './hooks/useAuth';

// Layout
import Layout from './components/Layout';
import ProtectedRoute, { BuyerRoute, SellerRoute, AdminRoute } from './components/ProtectedRoute';

// Public pages
import Landing from './pages/Landing';

// Auth pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { AuthCallback } from './pages/auth/AuthCallback';

// Seller pages
import SellerDashboard from './pages/seller/Dashboard';
import BuyerInterest from './pages/seller/BuyerInterest';
import SmartMatching from './pages/seller/SmartMatching';
import CompanyProfile from './pages/seller/CompanyProfile';
import NotificationSettings from './pages/seller/NotificationSettings';
import NotificationList from './pages/seller/NotificationList';
import AlertDetail from './pages/seller/AlertDetail';
import MatchDetails from './pages/seller/MatchDetails';
import SellerPosts from './pages/seller/Posts';
import SellerSettings from './pages/seller/Settings';
import DealIntelligence from './pages/seller/DealIntelligence';
import BDResources from './pages/seller/BDResources';

// Buyer pages
import BuyerDashboard from './pages/buyer/Dashboard';
import BuyerSearch from './pages/buyer/Search';
import SellerDetails from './pages/buyer/SellerDetails';
import BuyerProfile from './pages/buyer/Profile';
import BuyerSettings from './pages/buyer/Settings';
import BuyerMatches from './pages/buyer/Matches';
import BuyerFeed from './pages/buyer/Feed';
import OrganizationProfile from './pages/buyer/Organization';

// Shared pages
import Messages from './pages/Messages';
import PostDetail from './pages/PostDetail';

// Admin pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminIntroductions from './pages/admin/Introductions';
import AdminSellers from './pages/admin/Sellers';
import AdminBuyers from './pages/admin/Buyers';
import AdminUsers from './pages/admin/Users';
import AdminEmailSettings from './pages/admin/EmailSettings';
import AdminSettings from './pages/admin/Settings';
import AdminPosts from './pages/admin/Posts';
import AdminAnalytics from './pages/admin/Analytics';
import AdminCrawlerStatus from './pages/admin/CrawlerStatus';
import AdminSignals from './pages/admin/Signals';
import AdminHotAreas from './pages/admin/HotAreas';
import AdminSignalParser from './pages/admin/SignalParser';
import AdminDeals from './pages/admin/Deals';
import AdminCatalysts from './pages/admin/Catalysts';

function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Pharmunity...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />

      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin login (public - separate from main login) */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Browse routes - require buyer login (sellers shouldn't see competitors, public users must register) */}
      <Route path="/browse" element={<BuyerRoute><BuyerSearch /></BuyerRoute>} />
      <Route path="/company/:id" element={<BuyerRoute><SellerDetails /></BuyerRoute>} />
      <Route path="/post/:id" element={<PostDetail />} />

      {/* Seller routes (protected - sellers only) */}
      <Route
        path="/seller"
        element={
          <SellerRoute>
            <Layout userType="seller" />
          </SellerRoute>
        }
      >
        <Route index element={<Navigate to="/seller/dashboard" replace />} />
        <Route path="dashboard" element={<SellerDashboard />} />
        <Route path="bd-resources" element={<BDResources />} />
        <Route path="posts" element={<SellerPosts />} />
        <Route path="interest" element={<BuyerInterest />} />
        <Route path="matching" element={<SmartMatching />} />
        <Route path="profile" element={<CompanyProfile />} />
        <Route path="notifications" element={<NotificationList />} />
        <Route path="notifications/settings" element={<NotificationSettings />} />
        <Route path="alert/:alertId" element={<AlertDetail />} />
        <Route path="match/:matchId" element={<MatchDetails />} />
        <Route path="deal-intelligence" element={<DealIntelligence />} />
        <Route path="messages" element={<Messages />} />
        <Route path="settings" element={<SellerSettings />} />
      </Route>

      {/* Buyer routes (protected - buyers only) */}
      <Route
        path="/buyer"
        element={
          <BuyerRoute>
            <Layout userType="buyer" />
          </BuyerRoute>
        }
      >
        <Route index element={<Navigate to="/buyer/dashboard" replace />} />
        <Route path="dashboard" element={<BuyerDashboard />} />
        <Route path="feed" element={<BuyerFeed />} />
        <Route path="search" element={<BuyerSearch />} />
        <Route path="matches" element={<BuyerMatches />} />
        <Route path="company/:id" element={<SellerDetails />} />
        <Route path="messages" element={<Messages />} />
        <Route path="profile" element={<BuyerProfile />} />
        <Route path="settings" element={<BuyerSettings />} />
        <Route path="organization" element={<OrganizationProfile />} />
      </Route>

      {/* Admin routes (protected - admin only) */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Layout userType="admin" />
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="introductions" element={<AdminIntroductions />} />
        <Route path="introductions/:id" element={<AdminIntroductions />} />
        <Route path="sellers" element={<AdminSellers />} />
        <Route path="sellers/:id" element={<AdminSellers />} />
        <Route path="buyers" element={<AdminBuyers />} />
        <Route path="posts" element={<AdminPosts />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="email" element={<AdminEmailSettings />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="crawler" element={<AdminCrawlerStatus />} />
        <Route path="signals" element={<AdminSignals />} />
        <Route path="deals" element={<AdminDeals />} />
        <Route path="hot-areas" element={<AdminHotAreas />} />
        <Route path="signal-parser" element={<AdminSignalParser />} />
        <Route path="catalysts" element={<AdminCatalysts />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
